import { cycleRestrict } from '../../../common/utils/projection/math';

interface ILongitudeRange {
  west: number;
  east: number;
}

interface ILongitudeClampResult {
  range: ILongitudeRange;
  hasChanges: boolean;
}

const LONGITUDE_CYCLE = 360;
const MAX_LONGITUDE = 180;
const MIN_LONGITUDE = -180;
const LONGITUDE_SHIFTS = [-LONGITUDE_CYCLE, 0, LONGITUDE_CYCLE];

function normalizeLongitude(longitude: number): number {
  const normalized = cycleRestrict(longitude, MIN_LONGITUDE, MAX_LONGITUDE);

  return normalized === MIN_LONGITUDE ? MAX_LONGITUDE : normalized;
}

function toLongitudeRange(bounds: [YMaps.TCoord, YMaps.TCoord]): ILongitudeRange {
  const west = bounds[0][1];
  let east = bounds[1][1];

  if (east < west) {
    east += LONGITUDE_CYCLE;
  }

  return { west, east };
}

function getLongitudeClampShift(range: ILongitudeRange, restrictRange: ILongitudeRange): number {
  if (range.west < restrictRange.west) {
    return restrictRange.west - range.west;
  }

  if (range.east > restrictRange.east) {
    return restrictRange.east - range.east;
  }

  return 0;
}

function clampLongitudeRange(range: ILongitudeRange, restrictRange: ILongitudeRange): ILongitudeClampResult {
  const longitudeSpan = range.east - range.west;
  const restrictLongitudeSpan = restrictRange.east - restrictRange.west;

  if (longitudeSpan >= restrictLongitudeSpan) {
    return {
      range: restrictRange,
      hasChanges: range.west !== restrictRange.west || range.east !== restrictRange.east,
    };
  }

  const bestCandidate = LONGITUDE_SHIFTS.map(shift => {
    const shiftedRange = {
      west: range.west + shift,
      east: range.east + shift,
    };
    const clampShift = getLongitudeClampShift(shiftedRange, restrictRange);

    return {
      range: {
        west: shiftedRange.west + clampShift,
        east: shiftedRange.east + clampShift,
      },
      distance: Math.abs(clampShift),
      hasChanges: clampShift !== 0,
    };
  }).sort((left, right) => left.distance - right.distance)[0];

  if (!bestCandidate.hasChanges) {
    return { range, hasChanges: false };
  }

  return {
    range: bestCandidate.range,
    hasChanges: true,
  };
}

export function clampVisibleBoundsToRestrictArea(
  bounds: [YMaps.TCoord, YMaps.TCoord],
  restrictArea: [YMaps.TCoord, YMaps.TCoord],
): [YMaps.TCoord, YMaps.TCoord] | null {
  const [southWest, northEast] = bounds;
  const [restrictSouthWest, restrictNorthEast] = restrictArea;
  const latitudeSpan = northEast[0] - southWest[0];
  const restrictLatitudeSpan = restrictNorthEast[0] - restrictSouthWest[0];
  const longitudeRange = toLongitudeRange(bounds);
  const restrictLongitudeRange = toLongitudeRange(restrictArea);
  const longitudeClampResult = clampLongitudeRange(longitudeRange, restrictLongitudeRange);

  let nextSouth = southWest[0];
  let nextNorth = northEast[0];
  let hasLatitudeChanges = false;

  if (latitudeSpan >= restrictLatitudeSpan) {
    nextSouth = restrictSouthWest[0];
    nextNorth = restrictNorthEast[0];
    hasLatitudeChanges = nextSouth !== southWest[0] || nextNorth !== northEast[0];
  } else if (nextSouth < restrictSouthWest[0]) {
    const shift = restrictSouthWest[0] - nextSouth;
    nextSouth += shift;
    nextNorth += shift;
    hasLatitudeChanges = true;
  } else if (nextNorth > restrictNorthEast[0]) {
    const shift = restrictNorthEast[0] - nextNorth;
    nextSouth += shift;
    nextNorth += shift;
    hasLatitudeChanges = true;
  }

  if (!hasLatitudeChanges && !longitudeClampResult.hasChanges) {
    return null;
  }

  return [
    [nextSouth, normalizeLongitude(longitudeClampResult.range.west)],
    [nextNorth, normalizeLongitude(longitudeClampResult.range.east)],
  ];
}
